import { z } from 'zod';

/**
 * Admin "System health" screen (PRE-143 / PRE-264): last-24h LLM stats from
 * `llm_calls` plus Datadog Error Tracking / metrics when a read Application
 * key is bound. The SPA never talks to Datadog.
 */
export const adminSystemHealthIssueSchema = z.object({
  id: z.string(),
  title: z.string(),
  state: z.string(),
  events: z.number().nullable(),
  lastSeenMs: z.number().nullable(),
});

/** PRE-275: one Terraform-managed monitor and its current state. */
export const adminSystemHealthMonitorSchema = z.object({
  id: z.number(),
  name: z.string(),
  /** Datadog `overall_state`: OK · Warn · Alert · No Data · Skipped · Unknown. */
  status: z.string(),
  lastTriggeredMs: z.number().nullable(),
});

/** PRE-275: an error or killed-isolate log line. */
export const adminSystemHealthLogSchema = z.object({
  timestampMs: z.number().nullable(),
  message: z.string(),
  route: z.string().nullable(),
  errorCode: z.string().nullable(),
  traceId: z.string().nullable(),
  outcome: z.string().nullable(),
});

export const adminSystemHealthTrendPointSchema = z.object({
  tsMs: z.number(),
  value: z.number(),
});

/** PRE-275: a cron trigger or queue consumer, seen through its logs. */
export const adminSystemHealthJobSchema = z.object({
  name: z.string(),
  kind: z.enum(['cron', 'queue']),
  lastRunMs: z.number().nullable(),
  runs24h: z.number().int(),
  errors24h: z.number().int(),
});

export const adminSystemHealthResponseSchema = z.object({
  windowHours: z.number().int(),
  env: z.string(),
  llm: z.object({
    calls: z.number().int(),
    errors: z.number().int(),
    fallbacks: z.number().int(),
    fallbackRate: z.number(),
    p95LatencyMs: z.number().nullable(),
  }),
  datadog: z.object({
    configured: z.boolean(),
    error: z.string().nullable(),
    metrics: z.object({
      httpP95Ms: z.number().nullable(),
      llmCostUsd: z.number().nullable(),
      openIssueCount: z.number().int(),
    }),
    issues: z.array(adminSystemHealthIssueSchema),
    monitors: z.array(adminSystemHealthMonitorSchema),
    errorLogs: z.array(adminSystemHealthLogSchema),
    trends: z.object({
      requests: z.array(adminSystemHealthTrendPointSchema),
      errors: z.array(adminSystemHealthTrendPointSchema),
      p95Ms: z.array(adminSystemHealthTrendPointSchema),
    }),
    jobs: z.array(adminSystemHealthJobSchema),
  }),
});
export type AdminSystemHealthResponse = z.infer<typeof adminSystemHealthResponseSchema>;
